import './App.css';
import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import dealmetricsWordmark from './assets/dealmetrics-wordmark-dark.png';

// Route components are code-split via React.lazy so each chunk loads only when
// its route is first visited — shrinking the initial JS bundle.
const CreateProperty = lazy(() => import('./components/CreateProperty'));
const UpdateProperty = lazy(() => import('./components/UpdateProperty'));
const ArticlesListV6 = lazy(() => import('./components/ArticlesListV6'));
const ArticleDetailV4 = lazy(() => import('./components/ArticleDetailV4'));
const EditArticle = lazy(() => import('./components/EditArticle'));
const Lease = lazy(() => import('./components/Lease'));
const UpdateLease = lazy(() => import('./components/UpdateLease'));
const CreateLease = lazy(() => import('./components/CreateLease'));
const AddArticleForm = lazy(() => import('./components/article/AddArticleForm'));
const EditArticleForm = lazy(() => import('./components/article/EditArticleForm'));
const CustomArticleForm = lazy(() => import('./components/article/CustomArticleForm'));
const LeaseArticle = lazy(() => import('./components/LeaseArticle'));
const LeaseArticleDetail = lazy(() => import('./components/LeaseArticleDetail'));
const InvestmentSales = lazy(() => import('./components/InvestmentSales'));
const Mortgages = lazy(() => import('./components/Mortgages'));
const Sales = lazy(() => import('./components/Sales'));
const CreateSale = lazy(() => import('./components/CreateSale'));
const UpdateSale = lazy(() => import('./components/UpdateSale'));
const SaleArticle = lazy(() => import('./components/SaleArticle'));
const SaleArticleDetail = lazy(() => import('./components/SaleArticleDetail'));
const DashboardV2 = lazy(() => import('./components/DashboardV2'));
const Login = lazy(() => import('./components/Login'));
const CreateDeal = lazy(() => import('./components/CreateDeal'));
const ContactUs = lazy(() => import('./components/ContactUs'));

const PageLoader = () => (
  <div className="flex justify-center items-center min-h-[16rem]">
    <div
      className="animate-spin rounded-full h-12 w-12 border-2 border-t-transparent"
      style={{ borderColor: 'var(--dm-ink)', borderTopColor: 'transparent' }}
    />
  </div>
);

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoggedIn, logout } = useAuth();
  const isLoginPage = location.pathname === '/login';

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
  };

  const todayDate = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <div className="min-h-screen" style={{ background: 'var(--dm-bg)', fontFamily: 'var(--dm-sans)' }}>
      {!isLoginPage && (
        <header className="sticky top-0 z-[1000] border-b" style={{ background: 'rgba(250,250,250,.85)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', borderColor: 'var(--dm-border)' }}>
          <div className="max-w-[1340px] mx-auto px-7">
            {/* Primary nav bar */}
            <div className="flex justify-between items-center h-14">
              <Link to="/" className="flex items-center hover:opacity-90 transition-opacity" aria-label="dealmetrics.ai — Home">
                <img
                  src={dealmetricsWordmark}
                  alt="dealmetrics.ai"
                  className="h-7 sm:h-8 w-auto"
                  loading="eager"
                  decoding="async"
                />
              </Link>
              <nav className="hidden md:flex items-center gap-4" aria-label="Main navigation">
                <Link to="/" className="text-[11px] font-medium transition-colors hover:opacity-80" style={{ color: 'var(--dm-ink2)' }}>Home</Link>
                <Link to="/dashboard" className="text-[11px] font-medium transition-colors hover:opacity-80" style={{ color: 'var(--dm-ink2)' }}>Dashboard</Link>
                <Link to="/investment-sales" className="text-[11px] font-medium transition-colors hover:opacity-80" style={{ color: 'var(--dm-ink2)' }}>Investment Sales</Link>
                <Link to="/lease" className="text-[11px] font-medium transition-colors hover:opacity-80" style={{ color: 'var(--dm-ink2)' }}>Leases</Link>
                <Link to="/mortgages" className="text-[11px] font-medium transition-colors hover:opacity-80" style={{ color: 'var(--dm-ink2)' }}>Mortgages</Link>
                <Link to="/contact" className="text-[11px] font-medium transition-colors hover:opacity-80" style={{ color: 'var(--dm-ink2)' }}>Contact Us</Link>
                {isLoggedIn && (
                  <button type="button" onClick={handleLogout} className="text-[11px] font-medium transition-colors hover:opacity-80" style={{ color: 'var(--dm-ink2)' }}>Logout</button>
                )}
              </nav>
            </div>
            {/* Separator + date */}
            <div className="border-t py-3 text-center" style={{ borderColor: 'var(--dm-border)' }}>
              <time className="text-[11px]" style={{ color: 'var(--dm-muted)', fontFamily: 'var(--dm-mono)' }}>{todayDate}</time>
            </div>
          </div>
        </header>
      )}

      <main className="mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/" element={<ArticlesListV6 />} />
              <Route path="/contact" element={<ContactUs asPage />} />
              <Route path="/page/:page" element={<ArticlesListV6 />} />
              <Route path="/dashboard" element={<DashboardV2 />} />
              <Route path="/dashboard/:page" element={<DashboardV2 />} />
              <Route path="/investment-sales" element={<InvestmentSales />} />
              <Route path="/lease" element={<Lease />} />
              <Route path="/lease/:id/add-article" element={<AddArticleForm type="lease" />} />
              <Route path="/lease/:page" element={<Lease />} />
              <Route path="/sales" element={<Sales />} />
              <Route path="/sales/page/:page" element={<Sales />} />
              <Route path="/sales/:id/add-article" element={<AddArticleForm type="sale" />} />
              <Route path="/create-sale" element={<CreateSale />} />
              <Route path="/edit-sale/:id" element={<UpdateSale />} />
              <Route path="/sale-article" element={<SaleArticle />} />
              <Route path="/sale-article/page/:page" element={<SaleArticle />} />
              <Route path="/sale-article/:id" element={<SaleArticleDetail />} />
              <Route path="/edit-sale-article/:id" element={<EditArticleForm type="sale" />} />
              <Route path="/lease-article" element={<LeaseArticle />} />
              <Route path="/lease-article/page/:page" element={<LeaseArticle />} />
              <Route path="/lease-article/:id" element={<LeaseArticleDetail />} />
              <Route path="/edit-lease-article/:id" element={<EditArticleForm type="lease" />} />
              <Route path="/mortgages" element={<Mortgages />} />
              <Route path="/create-property" element={<CreateProperty />} />
              <Route path="/create-deal" element={<CreateDeal />} />
              <Route path="/edit-deal/:id" element={<CreateDeal />} />
              <Route path="/create-lease" element={<CreateLease />} />
              <Route path="/update-property/:id" element={<UpdateProperty />} />
              <Route path="/update-lease/:id" element={<UpdateLease />} />
              <Route path="/create-article" element={<CustomArticleForm mode="create" />} />
              <Route path="/edit-custom-article/:id" element={<CustomArticleForm mode="edit" />} />
              <Route path="/article/:id" element={<ArticleDetailV4 />} />
              <Route path="/edit-article/:id" element={<EditArticle />} />
              {/* Legacy routes kept for backward compatibility (resolve to current versions) */}
              <Route path="/news" element={<ArticlesListV6 />} />
              <Route path="/news-v3" element={<ArticlesListV6 />} />
              <Route path="/news-v4" element={<ArticlesListV6 />} />
              <Route path="/news-v5" element={<ArticlesListV6 />} />
              <Route path="/news-v6" element={<ArticlesListV6 />} />
              <Route path="/news-v6/:page" element={<ArticlesListV6 />} />
              <Route path="/article-v2/:id" element={<ArticleDetailV4 />} />
              <Route path="/article-v3/:id" element={<ArticleDetailV4 />} />
              <Route path="/article-v4/:id" element={<ArticleDetailV4 />} />
            </Routes>
            </Suspense>
            </ErrorBoundary>
          </div>
      </main>

      {/* Toast Container for global notifications */}
      <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={true}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
      />

      </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
